import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { HERKUNFT_WERTE, type FunnelPageType, type PatchFunnelResult } from './api';
import {
  type EditSession,
  saveEditSession,
  startEditSession,
  uploadFunnelImage,
} from './visualEditor/editorApi';
import ProduktBildDialog from './produktMedien/ProduktBildDialog';
import { type IframeNachricht, istIframeNachricht, sende } from './visualEditor/protocol';

/**
 * Seiten-Editor „auf der Seite" (Spec § 5, Builder B6).
 *
 * Das Editier-Dokument baut der Server (node-app/src/services/lpFunnel/editMode.ts): gespeicherter
 * Body + Seiten-CSS, OHNE Seiten-Skripte/Widget/GA/Consent/Pixel; Marker, <style>, <link> sind
 * durch gesperrte Kaesten ersetzt und bleiben serverseitig. Es laeuft in einem iframe `srcdoc` mit
 * `sandbox="allow-scripts"` OHNE allow-same-origin (opaker Ursprung, keine Cookies, kein Zugriff
 * aufs Dashboard). Die Editor-Runtime im iframe (editorRuntime.ts) spricht nur per postMessage;
 * Nachrichten werden nur angenommen, wenn event.source === iframe.contentWindow.
 *
 * Speichern: iframe serialisiert seinen Root → POST edit-save → Server setzt die Originale wieder
 * ein (fehlt/doppelt ein Kasten → 400; Seite inzwischen geaendert → 409; Sitzung abgelaufen → 410)
 * und schreibt ueber patchFunnelPage (neue Version 'editor'). Danach wird eine neue Sitzung geladen.
 *
 * Produkt-Medien (Spec .claude/plans/2026-10-08-produkt-medien.md § 12): „Aus Produkt-Medien …" in
 * der Bildleiste → Nachricht `produktBildWahl` → ProduktBildDialog → `produktBild` mit Bindung
 * `fach:variante` + aktueller URL zurueck ins iframe (setzt `data-lp-produktbild`).
 */
export interface VisualEditorProps {
  webinarId: string;
  pageType: FunnelPageType;
  /** Nach erfolgreichem Speichern (neue Version). */
  onSaved: (result: PatchFunnelResult) => void;
  /** Ungespeicherte Aenderungen ja/nein — der Dialog fragt vor dem Schliessen nach. */
  onDirtyChange?: (dirty: boolean) => void;
}

type Ansicht = 'alle' | 'vor' | 'nach' | `hk-${string}`;

const ANSICHTEN: Array<{ wert: Ansicht; label: string }> = [
  { wert: 'alle', label: 'Alle Abschnitte' },
  { wert: 'vor', label: 'Vor Stichtag' },
  { wert: 'nach', label: 'Nach Stichtag' },
  ...HERKUNFT_WERTE.map((h) => ({ wert: `hk-${h}` as Ansicht, label: `Herkunft: ${h}` })),
];

const SERIALISIER_TIMEOUT_MS = 8000;

export default function VisualEditor({
  webinarId,
  pageType,
  onSaved,
  onDirtyChange,
}: VisualEditorProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const bildIdRef = useRef<string | null>(null);
  const wartendRef = useRef(new Map<string, (html: string) => void>());
  const speichernRef = useRef<() => void>(() => undefined);
  const ansichtRef = useRef<Ansicht>('alle');

  const [session, setSession] = useState<EditSession | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<{
    text: string;
    art: 'info' | 'warning' | 'success';
  } | null>(null);
  const [bereit, setBereit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [undo, setUndo] = useState(0);
  const [speichert, setSpeichert] = useState(false);
  const [laedtBild, setLaedtBild] = useState(false);
  const [ansicht, setAnsicht] = useState<Ansicht>('alle');
  const [breite, setBreite] = useState<'desktop' | 'mobil'>('desktop');
  /** bildId des Bildes, fuer das gerade ein Produktbild gewaehlt wird (Dialog offen). */
  const [produktWahl, setProduktWahl] = useState<string | null>(null);

  const meldeDirty = useCallback(
    (d: boolean) => {
      setDirty(d);
      onDirtyChange?.(d);
    },
    [onDirtyChange],
  );

  const ladeSitzung = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    setBereit(false);
    meldeDirty(false);
    setUndo(0);
    const res = await startEditSession(webinarId, pageType);
    if (res.success && res.data) setSession(res.data);
    else {
      setSession(null);
      setFehler(res.error || 'Editor konnte nicht geladen werden');
    }
    setLaden(false);
  }, [webinarId, pageType, meldeDirty]);

  useEffect(() => {
    void ladeSitzung();
  }, [ladeSitzung]);

  // Nachrichten aus dem iframe — nur von genau diesem iframe.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || e.source !== win) return;
      if (!istIframeNachricht(e.data)) return;
      const m: IframeNachricht = e.data;
      switch (m.typ) {
        case 'bereit':
          setBereit(true);
          sende(win, { lpEdit: 1, typ: 'ansicht', wert: ansichtRef.current });
          break;
        case 'dirty':
          meldeDirty(Boolean(m.dirty));
          setUndo(Number(m.undo) || 0);
          break;
        case 'serialisiert': {
          const fn = wartendRef.current.get(String(m.reqId));
          if (fn && typeof m.html === 'string') {
            wartendRef.current.delete(String(m.reqId));
            fn(m.html);
          }
          break;
        }
        case 'bildErsetzen':
          bildIdRef.current = String(m.bildId);
          fileRef.current?.click();
          break;
        case 'produktBildWahl':
          setProduktWahl(String(m.bildId));
          break;
        case 'meldung':
          setHinweis({
            text: String(m.text).slice(0, 400),
            art: m.art === 'info' ? 'info' : 'warning',
          });
          break;
        case 'speichernWunsch':
          speichernRef.current();
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [meldeDirty]);

  useEffect(() => {
    ansichtRef.current = ansicht;
    sende(iframeRef.current?.contentWindow, { lpEdit: 1, typ: 'ansicht', wert: ansicht });
  }, [ansicht]);

  const serialisieren = useCallback((): Promise<string> => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return Promise.reject(new Error('Editor nicht geladen'));
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise<string>((resolve, reject) => {
      const t = window.setTimeout(() => {
        wartendRef.current.delete(reqId);
        reject(new Error('Editor antwortet nicht'));
      }, SERIALISIER_TIMEOUT_MS);
      wartendRef.current.set(reqId, (html) => {
        window.clearTimeout(t);
        resolve(html);
      });
      sende(win, { lpEdit: 1, typ: 'serialisieren', reqId });
    });
  }, []);

  const speichern = useCallback(async () => {
    if (!session || speichert) return;
    setSpeichert(true);
    setFehler(null);
    try {
      const bodyHtml = await serialisieren();
      const res = await saveEditSession(webinarId, pageType, {
        sessionKey: session.sessionKey,
        ausgangsHash: session.ausgangsHash,
        bodyHtml,
      });
      if (res.success && res.data) {
        const ergebnis: PatchFunnelResult = {
          warnings: res.data.warnings,
          versionId: res.data.versionId,
          contentHash: res.data.contentHash,
          bestandVersionId: res.data.bestandVersionId,
        };
        onSaved(ergebnis);
        const w = ergebnis.warnings?.length ? ` — Hinweise: ${ergebnis.warnings.join('; ')}` : '';
        setHinweis({ text: `Gespeichert (Version ${ergebnis.versionId})${w}`, art: 'success' });
        await ladeSitzung();
      } else {
        setFehler(res.error || 'Speichern fehlgeschlagen');
      }
    } catch (e) {
      setFehler((e as Error).message);
    } finally {
      setSpeichert(false);
    }
  }, [session, speichert, serialisieren, webinarId, pageType, onSaved, ladeSitzung]);

  useEffect(() => {
    speichernRef.current = () => {
      void speichern();
    };
  }, [speichern]);

  const onBildGewaehlt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const bildId = bildIdRef.current;
    bildIdRef.current = null;
    if (!file || !bildId) return;
    setLaedtBild(true);
    const r = await uploadFunnelImage(file);
    setLaedtBild(false);
    if (r.url)
      sende(iframeRef.current?.contentWindow, { lpEdit: 1, typ: 'bildUrl', bildId, url: r.url });
    else setHinweis({ text: `Bild-Upload fehlgeschlagen: ${r.error}`, art: 'warning' });
  };

  const neuLaden = () => {
    if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen und neu laden?')) return;
    void ladeSitzung();
  };

  const gesperrt = session?.locked ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <TextField
          select
          size="small"
          label="Ansicht"
          value={ansicht}
          onChange={(e) => setAnsicht(e.target.value as Ansicht)}
          sx={{ minWidth: 200 }}
        >
          {ANSICHTEN.map((a) => (
            <MenuItem key={a.wert} value={a.wert}>
              {a.label}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={breite}
          onChange={(_e, v) => v && setBreite(v)}
          aria-label="Breite"
        >
          <ToggleButton value="desktop">Desktop</ToggleButton>
          <ToggleButton value="mobil">Mobil</ToggleButton>
        </ToggleButtonGroup>
        <Button
          size="small"
          variant="outlined"
          disabled={!bereit || undo === 0}
          onClick={() =>
            sende(iframeRef.current?.contentWindow, { lpEdit: 1, typ: 'rueckgaengig' })
          }
        >
          Rückgängig
        </Button>
        <Button size="small" variant="outlined" onClick={neuLaden} disabled={laden || speichert}>
          Neu laden
        </Button>
        <Box sx={{ flex: 1 }} />
        {laedtBild && <Chip size="small" label="Bild wird hochgeladen …" />}
        {dirty ? (
          <Chip size="small" color="warning" label="Ungespeichert" />
        ) : (
          bereit && <Chip size="small" variant="outlined" label="Gespeichert" />
        )}
        <Tooltip title="Strg/Cmd + S im Editor">
          <span>
            <Button
              variant="contained"
              onClick={() => void speichern()}
              disabled={!bereit || !dirty || speichert}
            >
              {speichert ? <CircularProgress size={18} /> : 'Speichern'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {fehler && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={neuLaden}>
              Neu laden
            </Button>
          }
        >
          {fehler}
        </Alert>
      )}
      {hinweis && (
        <Alert severity={hinweis.art} onClose={() => setHinweis(null)}>
          {hinweis.text}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Text anklicken und tippen · Bild anklicken → Ersetzen / Aus Produkt-Medien / Alt-Text ·
        Abschnitt überfahren → Leiste rechts oben (verschieben, duplizieren, löschen, Sichtbarkeit).
        Gesperrte Kästen (
        {gesperrt.length
          ? gesperrt
              .map((l) => l.label)
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(', ')
          : '—'}
        ) ändert nur der HTML-Reiter.
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.100',
          display: 'flex',
          justifyContent: 'center',
          minHeight: 300,
        }}
      >
        {laden && (
          <Box sx={{ p: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!laden && session && (
          <iframe
            key={session.sessionKey}
            ref={iframeRef}
            title={`Seiten-Editor ${pageType}`}
            srcDoc={session.document}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            style={{
              border: 0,
              width: breite === 'mobil' ? 390 : '100%',
              height: '75vh',
              background: '#fff',
            }}
          />
        )}
      </Box>
      {produktWahl !== null && (
        <ProduktBildDialog
          webinarId={webinarId}
          onClose={() => setProduktWahl(null)}
          onWaehle={(bindung, url) => {
            sende(iframeRef.current?.contentWindow, {
              lpEdit: 1,
              typ: 'produktBild',
              bildId: produktWahl,
              bindung,
              url,
            });
            setProduktWahl(null);
          }}
        />
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={onBildGewaehlt}
      />
    </Box>
  );
}
